import PropTypes from 'prop-types';
import { useState } from 'react';
import {
	Box,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	Button,
	Link,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import ShoppingCartCheckoutOutlinedIcon from '@mui/icons-material/ShoppingCartCheckoutOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatUnits, formatQty } from 'app/common/numberFormat';

export const GREEN = '#196F3D';
export const DARK = '#252f3e';
const CYAN = 'rgb(34, 211, 238)';

const fmtTon = v => `${formatQty(v)} Ton`;

/** Encabezado oscuro unificado de la plataforma (banda + ícono cian + título). */
export const DialogHeader = ({ icon, title, subtitle }) => (
	<Box sx={{ background: `linear-gradient(135deg, ${DARK}, #2f3b4d)`, px: 3, py: 2.25 }}>
		<Stack direction="row" alignItems="center" spacing={1.5}>
			<Box sx={{ color: CYAN, display: 'inline-flex' }}>{icon}</Box>
			<Box>
				<Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 17, lineHeight: 1.25 }}>{title}</Typography>
				{subtitle && (
					<Typography
						sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12.5, mt: 0.25, wordBreak: 'break-all' }}
					>
						{subtitle}
					</Typography>
				)}
			</Box>
		</Stack>
	</Box>
);
DialogHeader.propTypes = { icon: PropTypes.node, title: PropTypes.node, subtitle: PropTypes.node };

/** Botón "Cerrar" unificado (outlined oscuro). */
export const closeBtnSx = {
	borderRadius: '10px',
	textTransform: 'none',
	fontWeight: 600,
	px: 3,
	borderColor: DARK,
	color: DARK,
	'&:hover': { backgroundColor: '#eef1f5', borderColor: DARK }
};

export const roundedPaper = { sx: { borderRadius: '16px', overflow: 'hidden' } };

/** Par etiqueta / valor compacto para el interior de cada tarjeta. */
export const Field = ({ label, value, span = 6 }) => (
	<Grid size={{ xs: 12, sm: span }}>
		<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
			{value ?? '—'}
		</Typography>
	</Grid>
);
Field.propTypes = {
	label: PropTypes.string,
	value: PropTypes.node,
	span: PropTypes.number
};

/** Tarjeta contenedora de una etapa de la cadena. */
const StageCard = ({ children, highlight }) => (
	<Paper
		variant="outlined"
		sx={{
			p: 1.5,
			mb: 1,
			borderRadius: 2,
			borderColor: highlight ? GREEN : 'divider',
			bgcolor: highlight ? 'rgba(25,111,61,0.04)' : 'background.paper'
		}}
	>
		{children}
	</Paper>
);
StageCard.propTypes = { children: PropTypes.node, highlight: PropTypes.bool };

/** Etapa de la línea de tiempo: punto con icono + contenido. */
const Stage = ({ index, title, subtitle, icon, color, isLast, children }) => (
	<TimelineItem>
		<TimelineSeparator>
			<TimelineDot sx={{ bgcolor: color, boxShadow: 'none' }}>{icon}</TimelineDot>
			{!isLast && <TimelineConnector />}
		</TimelineSeparator>
		<TimelineContent sx={{ pb: 3 }}>
			<Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
				<Chip label={index} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }} />
				<Typography variant="subtitle1" sx={{ fontWeight: 700, color: GREEN }}>
					{title}
				</Typography>
			</Stack>
			{subtitle && (
				<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
					{subtitle}
				</Typography>
			)}
			{children}
		</TimelineContent>
	</TimelineItem>
);
Stage.propTypes = {
	index: PropTypes.number,
	title: PropTypes.string,
	subtitle: PropTypes.string,
	icon: PropTypes.node,
	color: PropTypes.string,
	isLast: PropTypes.bool,
	children: PropTypes.node
};

/** Título de capítulo dentro de una subconsulta. */
const ChapterTitle = ({ children }) => (
	<Typography
		sx={{
			fontSize: 12.5,
			fontWeight: 700,
			color: DARK,
			textTransform: 'uppercase',
			letterSpacing: 0.5,
			mt: 2.5,
			mb: 1.25
		}}
	>
		{children}
	</Typography>
);
ChapterTitle.propTypes = { children: PropTypes.node };

const headCellSx = {
	fontWeight: 700,
	fontSize: 12,
	color: '#555',
	backgroundColor: '#f5f5f5',
	borderBottom: '2px solid #e0e0e0'
};

/**
 * Capítulo "Proceso de entrega del material": misma información que la consulta
 * "Detalle de entrega de material" (transacción, fecha, cantidad, mecanismos de
 * recolección y los ítems de origen con su ciudad y mecanismo).
 */
const DeliveryProcessChapter = ({ delivery }) => {
	if (!delivery) return null;
	const mechanisms = delivery.mechanisms || [];
	const origins = delivery.origins || [];

	return (
		<>
			<ChapterTitle>Proceso de entrega del material</ChapterTitle>
			<Grid container spacing={1.5}>
				<Field label="# de transacción" value={delivery.deliveredMaterialId} span={3} />
				<Field label="Fecha de entrega" value={delivery.deliveryDate || '—'} span={3} />
				<Field label="Material" value={delivery.materialType || '—'} span={3} />
				<Field label="Cantidad entregada" value={fmtTon(delivery.quantity)} span={3} />
			</Grid>

			{mechanisms.length > 0 && (
				<Box sx={{ mt: 1.5 }}>
					<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
						Mecanismos de recolección
					</Typography>
					<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
						{mechanisms.map(m => (
							<Chip key={m} label={m} size="small" variant="outlined" />
						))}
					</Stack>
				</Box>
			)}

			<ChapterTitle>Ítems de la entrega</ChapterTitle>
			{origins.length === 0 ? (
				<Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
					Sin ítems de origen registrados.
				</Typography>
			) : (
				<TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								{['Origen', 'Cantidad', 'Unidad', 'Mecanismo de recolección'].map((h, i) => (
									<TableCell key={h} align={i === 1 || i === 2 ? 'right' : 'left'} sx={headCellSx}>
										{h}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{origins.map(row => (
								<TableRow key={row.id} hover>
									<TableCell sx={{ fontSize: 13 }}>{row.city || '—'}</TableCell>
									<TableCell align="right" sx={{ fontSize: 13 }}>
										{formatQty(row.deliveredQuantity)}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: 13 }}>
										KG
									</TableCell>
									<TableCell sx={{ fontSize: 13 }}>{row.mechanism || '—'}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</>
	);
};
DeliveryProcessChapter.propTypes = { delivery: PropTypes.object };

const EmptyStage = () => (
	<Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
		Sin información registrada.
	</Typography>
);

/**
 * Línea de tiempo de la trazabilidad de un certificado de circularidad: las 4
 * etapas de la cadena (entrega y transformación → venta de material →
 * fabricación → venta de producto terminado) con su tarjeta de datos y la
 * subconsulta por certificado de transformación.
 *
 * La usan el popup de la plataforma (TraceabilityModal) y la consulta PÚBLICA
 * (PublicTraceabilityPage), para que ambas muestren exactamente lo mismo.
 */
const TraceabilityTimeline = ({ trace }) => {
	// Certificado de transformación seleccionado para la subconsulta (pop-up).
	const [detailCert, setDetailCert] = useState(null);

	const delivery = trace?.delivery || [];
	const materialSale = trace?.materialSale || [];
	const manufacture = trace?.manufacture || [];
	// Un certificado agrupado cubre varias ventas de producto: el backend las
	// entrega en `productSales`. `productSale` es el respaldo para una sola.
	const productSales = trace?.productSales?.length ? trace.productSales : [trace?.productSale].filter(Boolean);

	const totalManufacturedUnits = manufacture.reduce((s, f) => s + Number(f.units || 0), 0);
	const totalSoldUnits = productSales.reduce((s, p) => s + Number(p.unitsSold || 0), 0);
	const totalMaterialTons = Number(trace?.totalMaterialGrams || 0) / 1_000_000;

	return (
		<>
			<Timeline
				sx={{
					p: 0,
					m: 0,
					[`& .MuiTimelineItem-root:before`]: { flex: 0, padding: 0 }
				}}
			>
				<Stage
					index={1}
					title="Entrega y transformación de material"
					subtitle="Gestor → Transformador"
					color="#7D6608"
					icon={<LocalShippingOutlinedIcon fontSize="small" />}
				>
					{delivery.length === 0 ? (
						<EmptyStage />
					) : (
						delivery.map((d, i) => (
							<StageCard key={`${d.certificate}-${i}`}>
								<Grid container spacing={1.5} alignItems="center">
									<Field label="Certificado" value={d.certificate} />
									<Field label="Gestor" value={d.managerName} />
									<Field label="Cantidad" value={fmtTon(d.quantity)} span={3} />
									<Grid size={{ xs: 12, sm: 3 }} sx={{ textAlign: { sm: 'right' } }}>
										<Button size="small" variant="outlined" onClick={() => setDetailCert(d)}>
											Consultar
										</Button>
									</Grid>
								</Grid>
							</StageCard>
						))
					)}
				</Stage>

				<Stage
					index={2}
					title="Venta de material"
					subtitle="Transformador → Fabricante"
					color="#1F618D"
					icon={<SwapHorizOutlinedIcon fontSize="small" />}
				>
					{materialSale.length === 0 ? (
						<EmptyStage />
					) : (
						materialSale.map((m, i) => (
							<StageCard key={`${m.invoiceNumber}-${i}`}>
								<Grid container spacing={1.5}>
									<Field label="Transformador" value={m.transformer} />
									<Field label="Material vendido" value={m.soldMaterialName} />
									<Field label="Cantidad" value={fmtTon(m.totalQuantity)} />
									<Field
										label="Factura"
										value={
											m.invoiceLink ? (
												<Link
													href={m.invoiceLink}
													target="_blank"
													rel="noopener noreferrer"
													sx={{
														display: 'inline-flex',
														alignItems: 'center',
														gap: 0.5
													}}
												>
													<ReceiptLongOutlinedIcon fontSize="inherit" />
													{m.invoiceNumber || 'Ver factura'}
												</Link>
											) : (
												m.invoiceNumber
											)
										}
									/>
								</Grid>
							</StageCard>
						))
					)}
				</Stage>

				<Stage
					index={3}
					title="Fabricación"
					subtitle={
						manufacture.length > 0
							? `${formatUnits(totalManufacturedUnits)} und fabricadas en ${
									manufacture.length
							  } registro(s) · Material empleado total: ${formatQty(totalMaterialTons)} Ton`
							: undefined
					}
					color="#6C3483"
					icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
				>
					{manufacture.length === 0 ? (
						<EmptyStage />
					) : (
						manufacture.map((f, i) => (
							<StageCard key={`${f.product}-${f.period}-${i}`}>
								<Grid container spacing={1.5}>
									<Field label="Producto" value={f.product} />
									<Field label="Unidades" value={`${formatUnits(f.units)} und`} span={3} />
									<Field label="Periodo" value={f.period} span={3} />
									<Field
										label="Material empleado"
										value={`${formatQty(Number(f.materialGrams || 0) / 1_000_000)} Ton`}
										span={3}
									/>
								</Grid>
							</StageCard>
						))
					)}
				</Stage>

				<Stage
					index={4}
					title="Venta de producto terminado"
					subtitle={
						productSales.length > 1
							? `${productSales.length} certificados de venta · ${formatUnits(
									totalSoldUnits
							  )} und en total`
							: undefined
					}
					color={GREEN}
					icon={<ShoppingCartCheckoutOutlinedIcon fontSize="small" />}
					isLast
				>
					{productSales.length === 0 ? (
						<EmptyStage />
					) : (
						productSales.map((productSale, i) => (
							<StageCard key={productSale.serial || i} highlight>
								<Grid container spacing={1.5}>
									<Field label="Serial" value={productSale.serial} span={12} />
									<Field label="Producto" value={productSale.product} />
									<Field label="Material" value={productSale.material} />
									<Field
										label="Unidades vendidas"
										value={`${formatUnits(productSale.unitsSold)} und`}
										span={3}
									/>
									<Field label="Periodo" value={productSale.period} span={3} />
									<Field label="Cliente" value={productSale.clientName} />
									{productSale.invoiceLink && (
										<Field
											label="Factura"
											value={
												<Link
													href={productSale.invoiceLink}
													target="_blank"
													rel="noopener noreferrer"
													sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
												>
													<ReceiptLongOutlinedIcon fontSize="inherit" />
													Ver factura
												</Link>
											}
										/>
									)}
								</Grid>
							</StageCard>
						))
					)}
				</Stage>
			</Timeline>

			{/* Subconsulta (pop-up): trazabilidad de un certificado de transformación
			    con sus datos, la factura gestor→transformador y el proceso de entrega. */}
			<Dialog
				open={!!detailCert}
				onClose={() => setDetailCert(null)}
				maxWidth="md"
				fullWidth
				slotProps={{ paper: roundedPaper }}
			>
				<DialogHeader
					icon={<LocalShippingOutlinedIcon />}
					title="Trazabilidad del certificado de transformación"
				/>
				<DialogContent dividers>
					{detailCert && (
						<>
							<Grid container spacing={1.5}>
								<Field label="Certificado" value={detailCert.certificate} span={12} />
								<Field label="Gestor" value={detailCert.managerName} />
								<Field label="Transformador" value={detailCert.transformerName} />
								<Field label="Cantidad" value={fmtTon(detailCert.quantity)} />
								<Field label="Año de transformación" value={detailCert.transformationYear} />
								<Field label="Planta de transformación" value={detailCert.transformationPlant} />
								<Field label="Condición del material" value={detailCert.conditionType} />
								<Field
									label="Factura gestor → transformador"
									span={12}
									value={
										detailCert.invoiceLink ? (
											<Link
												href={detailCert.invoiceLink}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
											>
												<ReceiptLongOutlinedIcon fontSize="inherit" />
												{detailCert.invoiceNumber || 'Ver factura'}
											</Link>
										) : (
											detailCert.invoiceNumber || '—'
										)
									}
								/>
							</Grid>
							<DeliveryProcessChapter delivery={detailCert.delivery} />
						</>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2, justifyContent: 'center' }}>
					<Button onClick={() => setDetailCert(null)} variant="outlined" sx={closeBtnSx}>
						Cerrar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

TraceabilityTimeline.propTypes = { trace: PropTypes.object };

export default TraceabilityTimeline;
